// ONE GROUP ROW in the line table (owner, 2026-10-22 — D-396, D-399; G-19 to
// G-21). Drawn by `InvoiceLineItemsTable` in the table's own columns, with the
// table's own cell classes, `LockedValue` and its `DerivedMoney`, so a group
// row cannot drift from a line row:
//
//   * an expand arrow in the expand column, where # was, in the shared mark
//     box every leftmost mark takes (`useLineMarkStyles`, D-407): a blue icon
//     in a blue border (`markOutlined`, owner 2026-10-28 — D-424), the border
//     yellow while a line is flagged and red while the group blocks; gray and
//     disabled when the group is empty (D-423); no row fill (D-408);
//   * a checkbox where lines can be checked: checked when every one of its
//     lines is, mixed when some are, and a click checks or unchecks them all
//     (owner, 2026-10-22 — D-406);
//   * its name, editable where grouping may change, red while empty (D-406: a
//     group made by a drag has none, and it blocks until it is named);
//   * its Account, Department and Marketing Category LOCKED at every step and
//     read from its lines (D-406, superseding G-7's writing from here): the
//     value only once every line holds it, otherwise blank with the red border
//     — which blocks release — until they agree. A person changes the lines;
//   * its price the sum of its lines' totals and its quantity 1, locked (G-6);
//   * a flag icon while collapsed over a line with an unresolved flag (G-10),
//     its Additional Detail, read-only — the Marketing Category its lines
//     agree on, "See specific line items." for Fleet Maintenance's detail where
//     a line carries it, and the Associated Entities its lines share, red while
//     they differ (D-425); a tag goes on a line, never a group row (D-405,
//     D-409) — and Remove Group, its lines standing alone, only where a line's
//     own trash can shows (D-408);
//   * while expanded, its Account, Department, Price, Quantity and Total cells
//     empty — no value and no box — over its lines; collapsed, they show (owner,
//     2026-10-29 — D-439).
//
// THE TAX AND SHIPPING GROUPS (owner, 2026-10-27 — D-420) are drawn here too,
// with `charge`: the name fixed (never the name picker, never red), no Remove
// Group, and the three fields blank and never red, since a charge line carries
// none. An empty one (`empty`) — the placeholder the table draws when the
// invoice holds no such group yet — has its arrow and its box disabled.

import type { HTMLAttributes } from 'react'
import { Button, Checkbox, TableCell, TableRow, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { ChevronDownRegular, ChevronRightRegular, DeleteRegular } from '@fluentui/react-icons'
import type { AdditionalDetailSections, GroupField } from '@ap/shared'
import { AdditionalDetailPopover, type EntitiesSection } from './AdditionalDetailPopover'
import { GroupNamePicker } from './GroupNamePicker'
import { DerivedMoney } from './InvoiceLineItemsTable'
import { LockedValue } from './LockedValue'
import { toNumber } from '../lib/format'
import type { additionalDetailState } from '../lib/required'
import { useLineMarkStyles, useRequiredStyles } from '../lib/theme'


const useStyles = makeStyles({
  dropTarget: { outline: `2px dashed ${tokens.colorBrandStroke1}`, outlineOffset: '-2px' },
})

/** What one group row draws, read from its lines by the table. */
interface LineGroupRowModel {
  /** The stored group row's id, or the editor's key for a group not saved yet. */
  id: string
  key: string
  number: number | null
  description: string
  accountId: string | null
  accountLabel?: string
  departmentId: string | null
  departmentLabel?: string
  price: number | string | null
  total: number | string | null
  marketing: { categoryId: string | null; label: string }
  views: Record<GroupField, { value: string | null; red: boolean }>
  /** The stored group row, whose `code_name` the server copies from its lines. */
  item?: { code_name?: string | null; account_id?: string | null; department_id?: string | null }
}

/**
 * The Code column: the code's name, as a line row shows it (`lineItemCodingLabel`),
 * while the stored copy matches what is on screen; the bare code otherwise.
 */
function groupCodeLabel(g: LineGroupRowModel): string {
  if (!g.accountId || !g.departmentId) return '—'
  const stored = g.item
  if (stored?.code_name && stored.account_id === g.accountId && stored.department_id === g.departmentId) return stored.code_name
  return `${g.departmentId}-${g.accountId}`
}

/** The table's own cell classes, so the two kinds of row share them. */
interface LineTableCellClasses {
  selectCell: string
  cellInput: string
  codingCell: string
  codingCellInner: string
  /** One line with an ellipsis (D-471). */
  oneLine: string
  pickerStack: string
  num: string
  derivedNum: string
  iconCell: string
}

interface Props {
  group: LineGroupRowModel
  /** The invoice's Tax or Shipping group (D-420). */
  charge?: boolean
  /** No line in it yet: nothing to open or check (an empty Tax or Shipping group, D-420). */
  empty?: boolean
  classes: LineTableCellClasses
  columns: { select: boolean; expand: boolean; code: boolean; account: boolean; department: boolean; distributing: boolean }
  /**
   * The Associated Entities section of its Additional Detail: the tags its
   * lines share, read-only (D-409, D-425). Absent where lines show none.
   */
  entities?: EntitiesSection
  /** A line in the group carries Fleet Maintenance's detail (D-425). */
  fleetRollUp: boolean
  editing: boolean
  boxedFields: boolean
  locked: boolean
  busy: boolean
  open: boolean
  onToggle: () => void
  /** Where grouping may change: the name is editable. */
  grouping: boolean
  /**
   * Remove Group shows only where a line's own trash can does (owner,
   * 2026-10-23 — D-408: "I would just not offer the trash can at all on the
   * queue card if we are not going to offer it on every line").
   */
  removable: boolean
  /** The group's checkbox (D-406): its lines all checked, some, or none. */
  checked: boolean | 'mixed'
  onCheck: (checked: boolean) => void
  share?: { tax: number; shipping: number; total: number }
  /**
   * A line in the group carries an unresolved flag: the arrow wears yellow
   * (D-417). The group's own fields never do; the flagged line's field does
   * (owner, 2026-10-27).
   */
  flagged: boolean
  sections: AdditionalDetailSections
  detail: ReturnType<typeof additionalDetailState>
  accountName: (id: string | null) => string | undefined
  departmentName: (id: string | null) => string | undefined
  popupMountNode?: HTMLElement | null
  dropTarget: boolean
  rowProps: HTMLAttributes<HTMLTableRowElement>
  /** Extra classes for the row, e.g. no bottom hairline above a section rule (D-424). */
  className?: string
  /** The invoice's vendor, whose established groups the name picker offers (D-410). */
  vendorId: string | null
  /** The editor's rename; absent past the editor. */
  onRenameInput?: (name: string) => void
  /** A rename past the editor, on leaving the box. */
  onRenameCommit?: (name: string) => void
  onUngroup: () => void
}

export function LineGroupRow({
  group: g,
  charge = false,
  empty = false,
  classes: c,
  columns,
  entities,
  fleetRollUp,
  editing,
  boxedFields,
  locked,
  busy,
  open,
  onToggle,
  grouping,
  checked,
  onCheck,
  share,
  flagged,
  sections,
  detail,
  accountName,
  departmentName,
  popupMountNode,
  dropTarget,
  rowProps,
  className,
  onRenameInput,
  onRenameCommit,
  onUngroup,
  removable,
  vendorId,
}: Props) {
  const s = useStyles()
  const rs = useRequiredStyles()
  const marks = useLineMarkStyles()
  const live = !locked
  // RED wherever the gate still applies (not on a frozen record): a field the
  // lines do not all hold alike, or hold empty (D-406).
  const accountRed = live && g.views.account_id.red
  const departmentRed = live && g.views.department_id.red
  const nameRed = live && !charge && g.description.trim() === ''
  // RED ONLY: a field the group blocks on. A flag's yellow stays on the
  // flagged line's own field and on the arrow (owner, 2026-10-27).
  const box = (red: boolean) => (red ? rs.requiredEmpty : undefined)
  // THE ARROW'S RED (D-424): while any field of the group's own is red — its
  // name, Account, Department, or its Additional Detail.
  const groupRed = live && (nameRed || accountRed || departmentRed || detail.requiredMissing)
  // EXPANDED, THE FIVE FIELDS ARE EMPTY SPACE (D-439): the lines below carry
  // them. The cells stay, so the columns keep their widths.
  const blank = open
  return (
    <TableRow data-line-key={g.key} className={mergeClasses(dropTarget && s.dropTarget, className)} {...rowProps}>
      {columns.select && (
        <TableCell className={c.selectCell}>
          <Checkbox
            aria-label={`Select every line in the group ${g.description}`.trim()}
            checked={checked}
            disabled={busy || empty}
            onChange={(_e, d) => onCheck(d.checked === true)}
          />
        </TableCell>
      )}
      {columns.expand && (
        <TableCell>
          <Button
            className={mergeClasses(
              marks.mark,
              empty ? marks.markDisabled : marks.markOutlined,
              !empty && live && flagged && marks.markFlagged,
              !empty && groupRed && marks.markRequired,
            )}
            appearance="transparent"
            size="small"
            icon={open ? <ChevronDownRegular /> : <ChevronRightRegular />}
            aria-label={open ? `Collapse Group ${g.description}` : `Expand Group ${g.description}`}
            aria-expanded={open}
            disabled={empty}
            onClick={onToggle}
          />
        </TableCell>
      )}
      <TableCell>
        {grouping && !charge && (onRenameInput || onRenameCommit) ? (
          // THE VENDOR'S GROUPS, OR A NEW NAME (D-410): the bulk row's own picker.
          <GroupNamePicker
            ariaLabel="Group Description"
            placeholder="e.g. Poultry"
            value={g.description}
            vendorId={vendorId}
            mountNode={popupMountNode}
            disabled={locked || busy}
            invalid={nameRed}
            className={c.cellInput}
            onChange={(name) => {
              const next = name.trim()
              if (next === '' || next === g.description) return
              if (onRenameInput) onRenameInput(next)
              else onRenameCommit?.(next)
            }}
          />
        ) : (
          <LockedValue boxed={boxedFields} value={g.description} ariaLabel="Group Description" className={box(nameRed)} />
        )}
      </TableCell>
      {columns.code && (
        <TableCell className={c.codingCell}>
          <div className={mergeClasses(c.codingCellInner, c.oneLine)}>{groupCodeLabel(g)}</div>
        </TableCell>
      )}
      {/* LOCKED AT EVERY STEP (D-406): the value only once every line holds
          it; blank and red until then. */}
      {columns.account && (
        <TableCell className={c.codingCell}>
          {!blank && <div className={c.codingCellInner}>
            <div className={c.pickerStack}>
              <LockedValue
                boxed={boxedFields}
                value={g.accountId ? (g.accountLabel ?? accountName(g.accountId) ?? g.accountId) : ''}
                ariaLabel="Group Account"
                className={box(accountRed)}
               
              />
            </div>
          </div>}
        </TableCell>
      )}
      {columns.department && (
        <TableCell className={c.codingCell}>
          {!blank && <div className={c.codingCellInner}>
            <div className={c.pickerStack}>
              <LockedValue
                boxed={boxedFields}
                value={g.departmentId ? (g.departmentLabel ?? departmentName(g.departmentId) ?? g.departmentId) : ''}
                ariaLabel="Group Department"
                className={box(departmentRed)}
               
              />
            </div>
          </div>}
        </TableCell>
      )}
      {/* A Tax or Shipping row shows its Total alone: Price and Quantity blank,
          as its Account and Department are (owner, 2026-10-28). */}
      <TableCell className={c.num}>
        {blank ? null : charge ? (
          <LockedValue boxed={boxedFields} value="" align="money" ariaLabel="Group Price" />
        ) : (
          <DerivedMoney editing={editing} boxed={boxedFields} amount={g.price} ariaLabel="Group Price" />
        )}
      </TableCell>
      <TableCell className={c.num}>
        {!blank && <LockedValue boxed={boxedFields} value={charge ? '' : '1'} align="number" ariaLabel="Group Quantity" />}
      </TableCell>
      {columns.distributing && (
        <TableCell className={boxedFields ? c.num : c.derivedNum}>
          <DerivedMoney editing={editing} boxed={boxedFields} amount={share?.tax ?? 0} ariaLabel="Tax share" />
        </TableCell>
      )}
      {columns.distributing && (
        <TableCell className={boxedFields ? c.num : c.derivedNum}>
          <DerivedMoney editing={editing} boxed={boxedFields} amount={share?.shipping ?? 0} ariaLabel="Shipping share" />
        </TableCell>
      )}
      <TableCell className={c.num}>
        {!blank && (
          <DerivedMoney
            editing={editing}
            boxed={boxedFields}
            amount={columns.distributing ? (share?.total ?? toNumber(g.total)) : g.total}
            ariaLabel="Group Total"
          />
        )}
      </TableCell>
      <TableCell>
        <div className={c.iconCell}>
          {/* NO ADDITIONAL DETAIL ON A TAX OR SHIPPING ROW (owner, 2026-10-28 —
              D-423): it holds nothing a charge could owe. Its slot is kept, so
              the icon column lines up with every other row's. */}
          <span style={charge ? { visibility: 'hidden' } : undefined} aria-hidden={charge || undefined}>
          <AdditionalDetailPopover
            mountNode={popupMountNode}
            sections={sections}
            state={detail}
            marketing={{
              // Blank while its lines disagree (D-406).
              categoryId: detail.marketingInvalid ? null : g.marketing.categoryId,
              label: g.marketing.label,
              // Read-only (D-406): a line's own Additional Detail sets it.
              onSelect: undefined,
              saving: busy,
              pickerKey: 'group-marketing',
            }}
            transportation={null}
            transportationRollUp={fleetRollUp}
            entities={entities}
          />
          </span>
          {grouping && removable && !charge && (
            <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="Remove Group" title="Remove Group" onClick={onUngroup} />
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
